import { supabase } from '../../lib/supabase';
import { CrowdfundingCampaign, CampaignStatus } from '../../types/crowdfunding';

/**
 * Génère un slug à partir d'un titre
 */
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

export const campaignService = {
  /**
   * Récupère toutes les campagnes de crowdfunding avec filtrage optionnel et pagination
   */
  async getCampaigns(filters?: { 
    status?: CampaignStatus, 
    contestantId?: string,
    page?: number,
    pageSize?: number,
    searchTerm?: string,
    tenantId?: string
  }) {
    let query = supabase
      .from('crowdfunding_campaigns')
      .select(`
        *,
        contestant:contestants (
          name,
          photo_url,
          unique_code,
          contest:contests (
            name,
            slug
          )
        ),
        contributions:contributions(count)
      `, { count: 'exact' })
      .eq('contributions.payment_status', 'paid');

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.contestantId) {
      query = query.eq('contestant_id', filters.contestantId);
    }
    
    if (filters?.searchTerm) {
      query = query.or(`title.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
    }

    // Filtrer par tenant_id si fourni
    if (filters?.tenantId) {
      query = query.eq('tenant_id', filters.tenantId);
    }

    // Pagination
    if (filters?.page !== undefined && filters?.pageSize) {
      const from = filters.page * filters.pageSize;
      const to = from + filters.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des campagnes:', error);
      throw new Error(`Erreur lors de la récupération des campagnes: ${error.message}`);
    }

    return { 
      campaigns: data as CrowdfundingCampaign[], 
      totalCount: count || 0 
    };
  },

  /**
   * Récupère une campagne par son ID
   */
  async getCampaignById(id: string) {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .select(`
        *,
        contestant:contestants (
          name,
          photo_url,
          unique_code,
          contest:contests (
            name,
            slug
          )
        ),
        contributions:contributions(count)
      `)
      .eq('id', id)
      .eq('contributions.payment_status', 'paid')
      .single();

    if (error) {
      console.error(`Erreur lors de la récupération de la campagne ${id}:`, error);
      throw new Error(`Erreur lors de la récupération de la campagne: ${error.message}`);
    }

    return data as CrowdfundingCampaign;
  },

  /**
   * Récupère une campagne par son slug
   */
  async getCampaignBySlug(slug: string) {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .select(`
        *,
        contestant:contestants (
          name,
          photo_url,
          unique_code,
          contest:contests (
            name,
            slug
          )
        ),
        contributions:contributions(count)
      `)
      .eq('slug', slug)
      .eq('contributions.payment_status', 'paid')
      .single();

    if (error) {
      console.error(`Erreur lors de la récupération de la campagne avec le slug ${slug}:`, error);
      throw new Error(`Erreur lors de la récupération de la campagne: ${error.message}`);
    }

    return data as CrowdfundingCampaign;
  },

  /**
   * Crée une nouvelle campagne de crowdfunding
   */
  async createCampaign(campaign: Omit<CrowdfundingCampaign, 'id' | 'created_at' | 'updated_at' | 'collected_amount'>) {
    // Vérifier si le candidat existe
    const { data: contestant, error: contestantError } = await supabase
      .from('contestants')
      .select('id')
      .eq('id', campaign.contestant_id)
      .single();
      
    if (contestantError || !contestant) {
      throw new Error(`Le candidat sélectionné n'existe pas ou n'est pas valide`);
    }
    
    // Valider les dates
    const startDate = new Date(campaign.start_date);
    const endDate = new Date(campaign.end_date);
    const now = new Date();
    
    if (startDate > endDate) {
      throw new Error('La date de début doit être antérieure à la date de fin');
    }
    
    if (endDate < now && campaign.status === 'active') {
      throw new Error('Une campagne active ne peut pas avoir une date de fin dans le passé');
    }

    // Générer un slug à partir du titre
    const slug = generateSlug(campaign.title);
    
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .insert({
        contestant_id: campaign.contestant_id,
        title: campaign.title,
        description: campaign.description,
        goal_amount: campaign.goal_amount,
        collected_amount: 0, // Montant initial collecté est 0
        start_date: campaign.start_date,
        end_date: campaign.end_date,
        status: campaign.status || 'draft',
        slug: slug
      })
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la campagne:', error);
      throw new Error(`Erreur lors de la création de la campagne: ${error.message}`);
    }

    return data as CrowdfundingCampaign;
  },

  /**
   * Met à jour une campagne existante
   */
  async updateCampaign(id: string, updates: Partial<CrowdfundingCampaign>) {
    // Vérifier si la campagne existe
    const { data: existingCampaign, error: fetchError } = await supabase
      .from('crowdfunding_campaigns')
      .select('id, status')
      .eq('id', id)
      .single();
      
    if (fetchError || !existingCampaign) {
      throw new Error(`La campagne à mettre à jour n'existe pas`);
    }
    
    // Valider les dates si elles sont modifiées
    if (updates.start_date && updates.end_date) {
      const startDate = new Date(updates.start_date);
      const endDate = new Date(updates.end_date);
      
      if (startDate > endDate) {
        throw new Error('La date de début doit être antérieure à la date de fin');
      }
    }
    
    // Vérifier la cohérence du statut
    if (updates.status === 'active' && updates.end_date) {
      const endDate = new Date(updates.end_date);
      const now = new Date();
      
      if (endDate < now) {
        throw new Error('Une campagne active ne peut pas avoir une date de fin dans le passé');
      }
    }

    // Générer un nouveau slug si le titre est mis à jour
    let updatedData = { ...updates, updated_at: new Date().toISOString() };
    
    if (updates.title) {
      updatedData.slug = generateSlug(updates.title);
    }
    
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .update(updatedData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Erreur lors de la mise à jour de la campagne ${id}:`, error);
      throw new Error(`Erreur lors de la mise à jour de la campagne: ${error.message}`);
    }

    return data as CrowdfundingCampaign;
  },

  /**
   * Change le statut d'une campagne
   */
  async updateCampaignStatus(id: string, status: CampaignStatus) {
    // Vérifier si la campagne existe et récupérer ses dates
    const { data: campaign, error: fetchError } = await supabase
      .from('crowdfunding_campaigns')
      .select('id, start_date, end_date')
      .eq('id', id)
      .single();
      
    if (fetchError || !campaign) {
      throw new Error(`La campagne n'existe pas`);
    }
    
    // Valider la cohérence du statut avec les dates
    const now = new Date();
    const endDate = new Date(campaign.end_date);
    
    if (status === 'active' && endDate < now) {
      throw new Error('Une campagne ne peut pas être activée si sa date de fin est déjà passée');
    }
    
    return this.updateCampaign(id, { status });
  },

  /**
   * Supprime une campagne
   */
  async deleteCampaign(id: string) {
    // Vérifier si la campagne a des contributions
    const { count, error: countError } = await supabase
      .from('contributions')
      .select('id', { count: 'exact', head: true })
      .eq('campaign_id', id);
      
    if (countError) {
      throw new Error(`Erreur lors de la vérification des contributions: ${countError.message}`);
    }
    
    if (count && count > 0) {
      throw new Error(`Impossible de supprimer cette campagne car elle possède déjà ${count} contributions`);
    }

    const { error } = await supabase
      .from('crowdfunding_campaigns')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Erreur lors de la suppression de la campagne ${id}:`, error);
      throw new Error(`Erreur lors de la suppression de la campagne: ${error.message}`);
    }

    return true;
  },

  /**
   * Met à jour le montant collecté d'une campagne
   */
  async updateCollectedAmount(id: string, amount: number) {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .update({
        collected_amount: amount,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Erreur lors de la mise à jour du montant collecté pour la campagne ${id}:`, error);
      throw new Error(`Erreur lors de la mise à jour du montant collecté: ${error.message}`);
    }

    // Vérifier si l'objectif est atteint et mettre à jour le statut si nécessaire
    if (data.collected_amount >= data.goal_amount && data.status === 'active') {
      await this.updateCampaignStatus(id, 'completed');
    }

    return data as CrowdfundingCampaign;
  },

  /**
   * Vérifie si un candidat a déjà une campagne active
   */
  async hasActiveCampaign(contestantId: string) {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .select('id')
      .eq('contestant_id', contestantId)
      .in('status', ['draft', 'active'])
      .maybeSingle();

    if (error) {
      console.error(`Erreur lors de la vérification des campagnes pour le candidat ${contestantId}:`, error);
      throw new Error(`Erreur lors de la vérification des campagnes: ${error.message}`);
    }

    return !!data;
  },
  
  /**
   * Récupère les statistiques globales des campagnes
   */
  async getCampaignsStats(tenantId?: string) {
    // Récupérer les statistiques globales
    let query = supabase
      .from('crowdfunding_campaigns')
      .select('status, goal_amount, collected_amount, tenant_id');
    
    // Filtrer par tenant_id si fourni
    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }
    
    const { data: campaigns, error } = await query;
      
    if (error) {
      throw new Error(`Erreur lors de la récupération des statistiques: ${error.message}`);
    }
    
    // Récupérer le nombre total de contributions
    const { count: totalContributions, error: countError } = await supabase
      .from('contributions')
      .select('id', { count: 'exact', head: true })
      .eq('payment_status', 'paid');
      
    if (countError) {
      throw new Error(`Erreur lors du comptage des contributions: ${countError.message}`);
    }
    
    const stats = {
      totalCampaigns: campaigns.length,
      activeCampaigns: campaigns.filter(c => c.status === 'active').length,
      completedCampaigns: campaigns.filter(c => c.status === 'completed').length,
      totalGoalAmount: campaigns.reduce((sum, c) => sum + c.goal_amount, 0),
      totalCollectedAmount: campaigns.reduce((sum, c) => sum + c.collected_amount, 0),
      successRate: campaigns.length > 0 
        ? (campaigns.filter(c => c.status === 'completed').length / campaigns.length) * 100 
        : 0,
      totalContributions: totalContributions || 0
    };
    
    return stats;
  }
};
